import {
  JSONPatchDocument,
  PagedResponse,
  QueryParams,
} from "@ofqual/portal-core/core/types";
import { AnyAction } from "redux";
import { SocItemModel, RegulatorsToNotifyModel } from "../domain/SocItem";
import { SocItemDetailsOfqual } from "../domain/SocItemDetailsOfqual";
import { SocItemDetailsAo } from "../domain/SocItemDetailsAo";
import { SocEvent, SocEventComplete,  } from "../domain/SocEvent";
import { SocSubmitStatus } from "../domain/SocSubmitStatus";
import { MODULE_NAME } from "../globals";

export interface ActionPayload<T> extends AnyAction {
  type: string;
  payload: T;
}

export const LOADED_ITEMS = `${MODULE_NAME}/LOADED_ITEMS`;
export const LOADING_ITEMS = `${MODULE_NAME}/LOADING_ITEMS`;

export const LOADED_ITEM = `${MODULE_NAME}/LOADED_ITEM`;
export const LOADING_ITEM = `${MODULE_NAME}/LOADING_ITEM`;
export const SAVED_ITEM = `${MODULE_NAME}/SAVED_ITEM`;

export const UPDATING_ITEM = `${MODULE_NAME}/UPDATING_ITEM`;
export const UPDATING_ITEM_VALIDATION_FAIL = `${MODULE_NAME}/UPDATING_ITEM_VALIDATION_FAIL`;

export const INPUTTING_ITEM = `${MODULE_NAME}/INPUTTING_ITEM`;
export const CREATING_ITEM = `${MODULE_NAME}/CREATING_ITEM`;
export const CREATING_ITEM_VALIDATION_FAIL = `${MODULE_NAME}/CREATING_ITEM_VALIDATION_FAIL`;
export const CREATED_ITEM = `${MODULE_NAME}/CREATED_ITEM`;

export const ASSIGN_ITEM = `${MODULE_NAME}/ASSIGN_ITEM`;
export const UNASSIGN_ITEM = `${MODULE_NAME}/UNASSIGN_ITEM`;

export const LOADED_OFQUAL_ITEM = `${MODULE_NAME}/LOADED_OFQUAL_ITEM`;
export const LOADING_OFQUAL_ITEM = `${MODULE_NAME}/LOADING_OFQUAL_ITEM`;

export const UPDATING_OFQUAL_ITEM = `${MODULE_NAME}/UPDATING_OFQUAL_ITEM`;
export const UPDATED_OFQUAL_ITEM = `${MODULE_NAME}/UPDATED_OFQUAL_ITEM`;

export const LOADING_AO_ITEM = `${MODULE_NAME}/LOADING_AO_ITEM`;
export const LOADED_AO_ITEM = `${MODULE_NAME}/LOADED_AO_ITEM`;

export const UPDATING_AO_ITEM = `${MODULE_NAME}/UPDATING_AO_ITEM`;
export const UPDATED_AO_ITEM = `${MODULE_NAME}/UPDATED_AO_ITEM`;

export const UPDATING_REGULATORS_TO_NOTIFY_ITEM = `${MODULE_NAME}/UPDATING_REGULATORS_TO_NOTIFY_ITEM`;
export const UPDATED_REGULATORS_TO_NOTIFY_ITEM = `${MODULE_NAME}/UPDATED_REGULATORS_TO_NOTIFY_ITEM`;

export const SENDING_MESSAGE = `${MODULE_NAME}/SENDING_MESSAGE`;

export const ADD_EVENT = `${MODULE_NAME}/ADD_EVENT`;
export const COMPLETE_EVENT = `${MODULE_NAME}/COMPLETE_EVENT`;

export const SEND_INTERNAL_MESSAGE = `${MODULE_NAME}/SEND_INTERNAL_MESSAGE`;

export const ADD_CASE_NOTE = `${MODULE_NAME}/ADD_CASE_NOTE`;

export const RESUBMIT_ITEM = `${MODULE_NAME}/RESUBMIT_ITEM`;

export const SUBMIT_SOC_ITEM = `${MODULE_NAME}/SUBMIT_SOC_ITEM`;

export const CLOSE_ITEM = `${MODULE_NAME}/CLOSE_ITEM`;

export const REOPEN_ITEM = `${MODULE_NAME}/REOPEN_ITEM`;

export const loadedItems = (payload: PagedResponse<SocItemModel>) => ({
  type: LOADED_ITEMS,
  payload,
});
export const loadingItems = (queryParams: QueryParams, year: string) => ({
  type: LOADING_ITEMS,
  payload: { queryParams, year },
});

export const loadedItem = (item: SocItemModel) => ({
  type: LOADED_ITEM,
  payload: item,
});

export const loadingItem = (id: string, year: string) => ({
  type: LOADING_ITEM,
  payload: { id, year },
});

export const updatingItem = (id: string, patchDocument: JSONPatchDocument) => ({
  type: UPDATING_ITEM,
  payload: { id, patchDocument },
});

export const loadedOfqualItem = (payload: SocItemDetailsOfqual) => ({
  type: LOADED_OFQUAL_ITEM,
  payload,
});

export const loadingOfqualItem = (id: string, year: string) => ({
  type: LOADING_OFQUAL_ITEM,
  payload: { id, year },
});

export const updatingOfqualItem = (
  year: string,
  model: SocItemDetailsOfqual,
) => ({
  type: UPDATING_OFQUAL_ITEM,
  payload: { year, model },
});

export const updatedOfqualItem = (payload: SocItemDetailsOfqual) => ({
  type: UPDATED_OFQUAL_ITEM,
  payload,
});

export const loadedAoItem = (payload: SocItemDetailsAo) => ({
  type: LOADED_AO_ITEM,
  payload,
});

export const loadingAoItem = (id: string, year: string) => ({
  type: LOADING_AO_ITEM,
  payload: { id, year },
});

export const updatingAoItem = (year: string, model: SocItemDetailsAo) => ({
  type: UPDATING_AO_ITEM,
  payload: { year, model },
});

export const updatedAoItem = (payload: SocItemDetailsAo) => ({
  type: UPDATED_AO_ITEM,
  payload,
});

export const inputtingItem = () => ({
  type: INPUTTING_ITEM,
});

export const unassignCase = (id: string, year: string) => ({
  type: UNASSIGN_ITEM,
  payload: { id, year },
});

export const assignCase = (
  id: string,
  year: string,
  upn: string,
  displayName: string,
) => ({
  type: ASSIGN_ITEM,
  payload: { id, year, upn, displayName },
});

export const updatingRegulatorsToNotify = (
  id: string,
  year: string,
  model: RegulatorsToNotifyModel,
) => ({
  type: UPDATING_REGULATORS_TO_NOTIFY_ITEM,
  payload: { id, year, model },
});

export const updatedRegulatorsToNotify = (model: SocItemModel) => ({
  type: UPDATED_REGULATORS_TO_NOTIFY_ITEM,
  payload: { model },
});

export const sendMessage = (id: string, files: File[], message: string) => ({
  type: SENDING_MESSAGE,
  payload: { id, files, message },
});

export const sendInternalMessage = (
  id: string,
  files: File[],
  subject: string,
  message: string,
  recipients: string[],
) => ({
  type: SEND_INTERNAL_MESSAGE,
  payload: { id, files, subject, message, recipients },
});

export const addCaseNote = (
  id: string,
  files: File[],
  note: string,
  noteType: number,
) => ({
  type: ADD_CASE_NOTE,
  payload: { id, files, note, noteType },
});

export const addEvent = (id: string, newEvent: SocEvent) => ({
  type: ADD_EVENT,
  payload: { id, newEvent },
});

export const completeEvent = (
  id: string,
  eventId: string,
  completedEvent: SocEventComplete,
) => ({
  type: COMPLETE_EVENT,
  payload: { id, eventId, completedEvent },
});

export const enableResubmission = (id: string, year: string) => ({
  type: RESUBMIT_ITEM,
  payload: { id, year },
});

export const submitSocAction = (year: string, modelAo: SocItemDetailsAo, modelRegulator: RegulatorsToNotifyModel, saveAction: SocSubmitStatus) => ({
  type: SUBMIT_SOC_ITEM,
  payload: { year, modelAo, modelRegulator, saveAction },
});

export const closeSoc = (id: string, year: string, closeReason: string) => ({
  type: CLOSE_ITEM,
  payload: { id, year, closeReason },
});

export const reopenSoc = (id: string, year: string) => ({
  type: REOPEN_ITEM,
  payload: { id, year },
});

